import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../lib/apiClient";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { Badge } from "../components/ui/Badge";
import { Link } from "react-router-dom";

type OverviewResponse = {
  totals: {
    widgets: number;
    activeWidgets: number;
    analyticsCount: number;
  };
  analytics: Array<{
    widgetId: string;
    timeframe: string;
    capturedAt: number;
  }>;
};

export const OverviewPage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["analytics", "overview"],
    queryFn: async () => {
      const response = await apiClient.get<OverviewResponse>("/analytics/overview");
      return response.data;
    }
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-100">Realtime Command Center</h2>
        <p className="text-sm text-slate-400">
          Monitor live performance, agent availability, and widget engagement.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <p className="text-sm text-slate-400">Active Widgets</p>
            <h3 className="text-3xl font-semibold text-slate-100">
              {isLoading ? <Spinner size="sm" /> : data?.totals.activeWidgets ?? 0}
            </h3>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <p className="text-sm text-slate-400">Total Widgets</p>
            <h3 className="text-3xl font-semibold text-slate-100">
              {isLoading ? <Spinner size="sm" /> : data?.totals.widgets ?? 0}
            </h3>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <p className="text-sm text-slate-400">Captured Analytics</p>
            <h3 className="text-3xl font-semibold text-slate-100">
              {isLoading ? <Spinner size="sm" /> : data?.totals.analyticsCount ?? 0}
            </h3>
          </CardHeader>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-200">Recent Widget Activity</p>
              <p className="text-xs text-slate-400">
                Aggregated against Cloudflare Durable Object analytics stream.
              </p>
            </div>
            <Link to="/dashboard/widgets" className="text-sm text-brand hover:underline">
              Manage widgets
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : data && data.analytics.length > 0 ? (
            <div className="space-y-3">
              {data.analytics.slice(0, 6).map((entry) => (
                <div
                  key={`${entry.widgetId}-${entry.capturedAt}`}
                  className="flex items-center justify-between rounded-lg border border-slate-900 bg-slate-900/60 px-4 py-3"
                >
                  <div>
                    <p className="font-medium text-slate-100">{entry.widgetId}</p>
                    <p className="text-xs text-slate-500">
                      Bucket: {entry.timeframe} · Captured at{" "}
                      {new Date(entry.capturedAt).toLocaleString()}
                    </p>
                  </div>
                  <Badge variant="info">analytics</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No analytics captured yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
